/**
 * Layout of the Menú page (plan §5.2), decided from the page's own width
 * (the scroller's `clientWidth`, which the on-screen keyboard never
 * changes), never from the window height.
 */
export type TCatalogTier = 'phone' | 'tablet' | 'desktop';

export type TCatalogWidthStep = 0 | 1 | 2;

export type TCatalogRowMode = 'wide' | 'compact';

/** The list column is centred at most this wide. */
export const LIST_MAX_WIDTH = 1120;

/** Rows lay out wide from this list-column width. */
export const WIDE_ROW_MIN = 640;

/** Below this page width: phone. */
export const TABLET_MIN = 720;

/** From this page width: desktop. */
export const DESKTOP_MIN = 1136;

export function catalogTier(pageWidth: number): TCatalogTier {
  if (pageWidth < TABLET_MIN) return 'phone';
  if (pageWidth < DESKTOP_MIN) return 'tablet';
  return 'desktop';
}

/** Desktop only: wider pages get a wider rail and editor. */
export function catalogWidthStep(pageWidth: number): TCatalogWidthStep {
  if (pageWidth < 1600) return 0;
  if (pageWidth < 2100) return 1;
  return 2;
}

export interface ICatalogColumns {
  /** null = no rail (phones). */
  railWidth: number | null;
  /** null = the editor is a sheet. */
  asideWidth: number | null;
  asideDocked: boolean;
  gutter: 16 | 24;
  /**
   * Room at the end of the scroll for the fixed bottom bars (the selection
   * bar, the reorder footer) where they overlap the content.
   */
  bottomPad: 24 | 88;
}

const RAIL_WIDTHS = [232, 256, 280] as const;
const ASIDE_WIDTHS = [480, 520, 600] as const;

export interface ICatalogColumnsOptions {
  /**
   * The «Asistente» panel is docked on the right: one right-hand panel at a
   * time, so the editor opens as its sheet inside the page (plan §9.3).
   */
  assistantDocked?: boolean;
}

export function catalogColumns(
  tier: TCatalogTier,
  step: TCatalogWidthStep,
  options: ICatalogColumnsOptions = {}
): ICatalogColumns {
  if (tier === 'phone')
    return {
      railWidth: null,
      asideWidth: null,
      asideDocked: false,
      gutter: 16,
      bottomPad: 88,
    };
  if (tier === 'tablet')
    return {
      railWidth: 208,
      asideWidth: null,
      asideDocked: false,
      gutter: 16,
      bottomPad: 88,
    };
  const asideDocked = !options.assistantDocked;
  return {
    railWidth: RAIL_WIDTHS[step],
    asideWidth: asideDocked ? ASIDE_WIDTHS[step] : null,
    asideDocked,
    gutter: 24,
    bottomPad: 24,
  };
}

export function catalogRowMode(listWidth: number): TCatalogRowMode {
  return listWidth >= WIDE_ROW_MIN ? 'wide' : 'compact';
}

/**
 * True while the focused element sits in a subtree that the next tier would
 * unmount (`data-tier-only` lists the tiers it exists in): unmounting it
 * would close the on-screen keyboard (AGENTS.md adaptive layout rule).
 */
export function mustDeferTier(
  focusedScopes: readonly string[] | null,
  nextTier: TCatalogTier
): boolean {
  return !!focusedScopes && !focusedScopes.includes(nextTier);
}
